import React from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { Box } from 'roo-ui/components';
import GoogleMap from 'components/maps/GoogleMap';
import GoogleMapMarker from 'components/maps/GoogleMapMarker';
import { useBreakpoints } from 'hooks/useBreakpoints';

const PRELOAD_DISTANCE = '1000px';

const createOptions = ({ latitude, longitude }) => ({
  disableDefaultUI: false,
  zoom: 16,
  center: { lat: latitude, lng: longitude },
  mapTypeControl: true,
  streetViewControl: true,
  styles: [
    {
      featureType: 'poi.place_of_worship',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.business',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.government',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.medical',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'poi.school',
      stylers: [{ visibility: 'off' }],
    },
  ],
});

const MapWithMarker = ({ latitude, longitude, lazy, name, onError }) => {
  latitude = Number(latitude);
  longitude = Number(longitude);
  const options = createOptions({ latitude, longitude });
  const { isLessThanBreakpoint } = useBreakpoints();
  const [ref, inView] = useInView({
    rootMargin: `${PRELOAD_DISTANCE} 0px ${PRELOAD_DISTANCE} 0px`,
    triggerOnce: true,
  });

  /**
   * NOTE:
   * If the component is not in view, we still need to render a base component,
   * so that the intersection observer can calculate when it is the correct distance
   * to begin preloading.
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Creating_an_intersection_observer
   */
  if (lazy && !inView) {
    return <Box data-testid="google-map-placeholder" ref={ref} />;
  }

  return (
    <Box height="100%" data-testid="google-map-container" ref={ref}>
      <GoogleMap
        name={name}
        zoom={16}
        center={{ lat: latitude, lng: longitude }}
        width="100%"
        height="100%"
        options={isLessThanBreakpoint(0) ? { ...options, disableDefaultUI: true } : options}
        onError={onError}
      >
        <GoogleMapMarker lat={latitude} lng={longitude} />
      </GoogleMap>
    </Box>
  );
};

MapWithMarker.propTypes = {
  lazy: PropTypes.bool,
  name: PropTypes.string.isRequired,
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onError: PropTypes.func,
};

MapWithMarker.defaultProps = {
  lazy: false,
  onError: () => {},
};

export default MapWithMarker;
